import axios, { AxiosResponse } from "axios";
import { map, Observable } from "rxjs";
import zustand from "zustand";
import { isResponseUnauthorized, useAccountStore } from "./account";
import { apiUrl } from "./apiUrl";

let lastErrorId = 0;

export interface ErrorData {
    id: number;
    status?: number;
    message?: string;
}

export interface CommStore {
    requestCount: number;
    isCommunicating: boolean;
    isCommunicatingForLongTime: boolean;
    timer: number | null;
    errors: ErrorData[];
    requestStarted(): void;
    requestFinished(error: any): void;
    handleError(error: any): Promise<void>;
    clearError(error: ErrorData): void;
}

export const useCommStore = zustand<CommStore>((set, get) => ({
    isCommunicating: false,
    isCommunicatingForLongTime: false,
    requestCount: 0,
    timer: null,
    errors: [],
    requestStarted() {
        setTimeout(() => {
            const timer = get().isCommunicating ? get().timer : window.setTimeout(() => {
                set({ timer: null, isCommunicatingForLongTime: true });
            }, 100);
            set(store => ({ requestCount: store.requestCount + 1, isCommunicating: true, timer }));
        });
    },
    async requestFinished(error: any) {
        setTimeout(() => {
            const requestCount = get().requestCount - 1;
            const isCommunicating = requestCount > 0;
            const isCommunicatingForLongTime = isCommunicating && get().isCommunicatingForLongTime;
            let timer = get().timer;
            if (!isCommunicating && timer != null) {
                window.clearTimeout(timer);
                timer = null;
            }

            set({ requestCount, isCommunicating, isCommunicatingForLongTime, timer });

            get().handleError(error);
        });
    },
    async handleError(error) {
        if (error != null && !isErrorCanceled(error) && !isError401(error)) {
            const status = error.response?.status;
            const message = await getErrorMessage(error);
            if (get().errors.some(p => p.status === status && p.message === message)) {
                return;
            }
            set({
                errors: get().errors.concat({
                    id: ++lastErrorId,
                    status,
                    message,
                })
            });
        }
    },
    clearError(error) {
        set({ errors: get().errors.filter(p => p !== error) });
    },
}));

export function isErrorCanceled(error: any) {
    return error?.code === "ERR_CANCELED";
}

export function isError401(error: any) {
    return error?.response?.status === 401;
}

export function isError4xx(error: any) {
    const status = error?.response?.status;
    return status >= 400 && status < 500;
}

export async function getErrorMessage(error: any): Promise<string> {
    return ((error.response?.data instanceof Blob ? await error.response?.data.text() : error.response?.data) ?? error.message) ?? "Unknown error";
}

export function getErrorCodeText(error: any) {
    return error.response?.status ? ` (code ${error.response.status})` : "";
}

export function createAxios(isTokenRefreshAllowed = true, handleError = true, accessToken?: string | null) {
    if (accessToken === undefined) {
        accessToken = isTokenRefreshAllowed ? useAccountStore.getState().getAndRefreshAccessToken() : useAccountStore.getState().data?.accountData.accessToken;
    }
    const commStore = useCommStore.getState();
    const inst = axios.create({
        baseURL: apiUrl(),
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    });
    inst.interceptors.request.use(ok => {
        commStore.requestStarted();
        return ok;
    }, err => {
        return Promise.reject(err);
    });
    inst.interceptors.response.use(ok => {
        commStore.requestFinished(undefined);
        return ok;
    }, err => {
        commStore.requestFinished(handleError ? err : undefined);
        if (isResponseUnauthorized(err)) {
            useAccountStore.getState().setData(null);
        }
        return Promise.reject(err);
    });
    return inst;
}

export function axiosObservable<T>(createResponse: (abortSignal: AbortSignal) => Promise<AxiosResponse<T>>) {
    return axiosObservableRaw(createResponse).pipe(map(p => p.data));
}

export function axiosObservableRaw<T>(createResponse: (abortSignal: AbortSignal) => Promise<AxiosResponse<T>>) {
    return new Observable<AxiosResponse<T>>(subscriber => {
        const abort = new AbortController();
        createResponse(abort.signal)
            .then(response => {
                subscriber.next(response);
                subscriber.complete();
            }, err => {
                subscriber.error(err);
            });
        return () => {
            abort.abort();
        };
    });
}
